import React, { useRef, useCallback, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICONS } from '../../icons';
import './modalShell.css';

/**
 * ModalShell - Modern modal wrapper with glass morphism + mouse glow
 * @param {boolean} isOpen - Whether the modal is visible
 * @param {function} onClose - Called when overlay or close button clicked
 * @param {string} size - 'sm' | 'md' | 'lg' | 'xl' (default: 'md')
 * @param {React.ReactNode} header - Custom header content (replaces default title)
 * @param {string} title - Simple title text (used when header not provided)
 * @param {React.ReactNode} footer - Footer content (action buttons)
 * @param {string} className - Additional class on the dialog
 * @param {React.ReactNode} children - Modal body content
 */
export default function ModalShell({
  isOpen,
  onClose,
  size = 'md',
  header,
  title,
  footer,
  className = '',
  children
}) {
  const dialogRef = useRef(null);

  const handleMouseMove = useCallback((e) => {
    if (!dialogRef.current) return;
    const rect = dialogRef.current.getBoundingClientRect();
    dialogRef.current.style.setProperty('--mouse-x', `${e.clientX - rect.left}px`);
    dialogRef.current.style.setProperty('--mouse-y', `${e.clientY - rect.top}px`);
  }, []);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const showDefaultHeader = title && !header;

  return (
    <div className="ms-overlay" onClick={handleOverlayClick}>
      <div
        ref={dialogRef}
        className={`ms-dialog ms-${size} ${className}`}
        onMouseMove={handleMouseMove}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ms-glow-surface" />
        <div className="ms-glow-border" />

        {(header || showDefaultHeader) && (
          <div className="ms-header">
            {header || (
              <>
                <h3 className="ms-title">{title}</h3>
                <button className="ms-close" onClick={onClose}>
                  <FontAwesomeIcon icon={ICONS.TIMES} />
                </button>
              </>
            )}
            {header && (
              <button className="ms-close" onClick={onClose}>
                <FontAwesomeIcon icon={ICONS.TIMES} />
              </button>
            )}
          </div>
        )}

        <div className="ms-body">
          {children}
        </div>

        {footer && (
          <div className="ms-footer">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
